import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Invoice, QboBatchItemResult, QboLineMapping } from '@catalyst-psa/shared';
import {
  INVOICE_DOCUMENT_TYPE_LABELS,
  isInvoiceEditable,
  QBO_MAPPING_SOURCE_LABELS,
} from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { ApiError } from '../../api/client.js';
import { useDetailOverlay } from '../../components/layout/DetailOverlay.js';
import { nextInvoiceInSequence } from './invoiceSequence.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { useTrackRecent } from '../quick-access/useTrackRecent.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { TabNav } from '../../components/ui/TabNav.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import { formatDate } from '../../utils/date.js';
import { useBackNavigation } from '../../utils/navigation.js';
import { useProductOptions } from '../products/api.js';
import { ProductPicker } from '../products/ProductPicker.js';
import {
  useQboInvoiceMapping,
  useQboSendPreview,
  useRetryQboInvoiceEmail,
  useSendInvoiceToQbo,
} from '../qbo/api.js';
import { QboSyncLogPanel } from '../qbo/QboSyncLogPanel.js';
import {
  useAddInvoiceLine,
  useClearCustomerBillingReminder,
  useClearNeedsChange,
  useCloseInvoice,
  useDeleteInvoice,
  useHoldInvoice,
  useInvoiceReviewers,
  useInvoiceTimeEntries,
  useMarkBilledAndRemoveInvoice,
  usePreviewInvoicePdf,
  useInvoice,
  useRefreshQboMapping,
  useReleaseInvoiceHold,
  useRemoveInvoiceLine,
  useReopenInvoice,
  useReorderInvoiceLines,
  useRequestInvoiceChanges,
  useSendToReview,
  useUpdateInvoice,
  useUpdateInvoiceLine,
  useVoidInvoice,
  useWriteOffTimeEntry,
} from './api.js';
import {
  INVOICE_PAYMENT_STATUS_LABELS,
  INVOICE_PAYMENT_STATUS_TONES,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_TONES,
  INVOICE_LINE_KIND_LABELS,
} from './status.js';
import { InvoiceFormModal } from './InvoiceFormModal.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Reviewers reachable via the one-click "Send to …" actions. */
const DAMON_EMAIL = 'damon@nscatalyst.com';
const JEFF_EMAIL = 'jeff@nscatalyst.com';

function errorMessage(error: unknown, fallback: string): string | null {
  if (!error) {
    return null;
  }
  return error instanceof ApiError ? error.message : fallback;
}

interface DetailItemProps {
  label: string;
  value: string;
}

function DetailItem({ label, value }: DetailItemProps): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}

interface InvoiceLinesProps {
  invoice: Invoice;
  /** Draft-only line editing (add / edit / remove) for members with invoice write access. */
  canEditLines: boolean;
}

interface LineDraft {
  description: string;
  quantity: string;
  unitRate: string;
}

const EMPTY_ADD_DRAFT = { productId: '', description: '', quantity: '1', unitRate: '' };

const LINE_INPUT_CLASS =
  'w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

function InvoiceLines({ invoice, canEditLines }: InvoiceLinesProps): JSX.Element {
  const lines = invoice.lines ?? [];
  const mappingQuery = useQboInvoiceMapping(invoice.id);
  const mappingByLine = useMemo(() => {
    const map = new Map<string, QboLineMapping>();
    for (const m of mappingQuery.data?.lines ?? []) {
      map.set(m.lineId, m);
    }
    return map;
  }, [mappingQuery.data]);

  const productsQuery = useProductOptions();
  const products = productsQuery.data ?? [];
  const addMutation = useAddInvoiceLine();
  const updateMutation = useUpdateInvoiceLine();
  const removeMutation = useRemoveInvoiceLine();
  const reorderMutation = useReorderInvoiceLines();
  const refreshMappingMutation = useRefreshQboMapping();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<LineDraft>({
    description: '',
    quantity: '',
    unitRate: '',
  });
  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState(EMPTY_ADD_DRAFT);
  const [removeTarget, setRemoveTarget] = useState<NonNullable<Invoice['lines']>[number] | null>(
    null,
  );

  const startEdit = (line: NonNullable<Invoice['lines']>[number]): void => {
    setEditingId(line.id);
    setEditDraft({
      description: line.description,
      quantity: line.quantity,
      unitRate: line.unitRate,
    });
  };

  const saveEdit = (): void => {
    if (!editingId || !editDraft.description.trim()) return;
    updateMutation.mutate(
      {
        id: invoice.id,
        lineId: editingId,
        input: {
          description: editDraft.description.trim(),
          quantity: editDraft.quantity.trim() || '0',
          unitRate: editDraft.unitRate.trim() || '0',
        },
      },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const saveAdd = (): void => {
    if (!addDraft.description.trim()) return;
    addMutation.mutate(
      {
        id: invoice.id,
        input: {
          productId: addDraft.productId || null,
          description: addDraft.description.trim(),
          quantity: addDraft.quantity.trim() || '1',
          unitRate: addDraft.unitRate.trim() || '0',
        },
      },
      {
        onSuccess: () => {
          setAdding(false);
          setAddDraft(EMPTY_ADD_DRAFT);
        },
      },
    );
  };

  const handleAddProductChange = (productId: string): void => {
    const product = products.find((p) => p.id === productId);
    setAddDraft((prev) => ({
      ...prev,
      productId,
      ...(product ? { description: product.name, unitRate: product.unitPrice } : {}),
    }));
  };

  /** Moves a line up/down and persists the new order so the document follows it. */
  const moveLine = (index: number, direction: -1 | 1): void => {
    const target = index + direction;
    if (target < 0 || target >= lines.length) return;
    const orderedIds = lines.map((l) => l.id);
    [orderedIds[index], orderedIds[target]] = [orderedIds[target]!, orderedIds[index]!];
    reorderMutation.mutate({ id: invoice.id, orderedIds });
  };

  const lineError =
    addMutation.isError ||
    updateMutation.isError ||
    removeMutation.isError ||
    reorderMutation.isError ||
    refreshMappingMutation.isError
      ? errorMessage(
          addMutation.error ??
            updateMutation.error ??
            removeMutation.error ??
            reorderMutation.error ??
            refreshMappingMutation.error,
          'Failed to update the invoice lines.',
        )
      : null;

  const hasTimeLines = lines.some((line) => line.kind === 'time');

  const cols = canEditLines ? 8 : 7;

  return (
    // Clipping is dropped while adding so the product picker's dropdown isn't cut off.
    <div
      className={`rounded-xl border border-slate-200 bg-white shadow-sm ${
        adding ? '' : 'overflow-hidden'
      }`}
    >
      {lineError ? (
        <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {lineError}
        </div>
      ) : null}
      {canEditLines && hasTimeLines ? (
        <div className="flex items-center justify-end gap-3 border-b border-slate-200 px-4 py-2">
          {refreshMappingMutation.data ? (
            <span className="text-xs text-emerald-700">
              {refreshMappingMutation.data.updatedLines > 0
                ? `Updated ${refreshMappingMutation.data.updatedLines} line${
                    refreshMappingMutation.data.updatedLines === 1 ? '' : 's'
                  } from the current SKU/class settings.`
                : 'All time lines already match the current SKU/class settings.'}
            </span>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => refreshMappingMutation.mutate(invoice.id)}
            isLoading={refreshMappingMutation.isPending}
            title="Restamp time lines with the QBO SKU and class from the current service category / ticket type settings"
          >
            Refresh QBO mapping
          </Button>
        </div>
      ) : null}
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3 font-medium">Kind</th>
            <th className="px-4 py-3 font-medium">Description</th>
            <th className="px-4 py-3 font-medium">QBO SKU</th>
            <th className="px-4 py-3 font-medium">QBO class</th>
            <th className="px-4 py-3 text-right font-medium">Qty</th>
            <th className="px-4 py-3 text-right font-medium">Rate</th>
            <th className="px-4 py-3 text-right font-medium">Amount</th>
            {canEditLines ? <th className="px-4 py-3 font-medium" /> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lines.length === 0 ? (
            <tr>
              <td colSpan={cols} className="px-4 py-6 text-center text-sm text-slate-500">
                This invoice has no lines.
              </td>
            </tr>
          ) : (
            lines.map((line, index) => {
              const mapping = mappingByLine.get(line.id);
              if (canEditLines && editingId === line.id) {
                const amountPreview =
                  Number(editDraft.quantity || '0') * Number(editDraft.unitRate || '0');
                return (
                  <tr key={line.id} className="bg-slate-50">
                    <td className="px-4 py-3">
                      <Badge tone="gray">{INVOICE_LINE_KIND_LABELS[line.kind]}</Badge>
                    </td>
                    <td className="px-4 py-2" colSpan={3}>
                      <input
                        value={editDraft.description}
                        onChange={(e) =>
                          setEditDraft((p) => ({ ...p, description: e.target.value }))
                        }
                        className={LINE_INPUT_CLASS}
                        aria-label="Line description"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        value={editDraft.quantity}
                        onChange={(e) => setEditDraft((p) => ({ ...p, quantity: e.target.value }))}
                        className={`${LINE_INPUT_CLASS} text-right`}
                        aria-label="Quantity"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        value={editDraft.unitRate}
                        onChange={(e) => setEditDraft((p) => ({ ...p, unitRate: e.target.value }))}
                        className={`${LINE_INPUT_CLASS} text-right`}
                        aria-label="Unit rate"
                      />
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">
                      {Number.isFinite(amountPreview) ? currency.format(amountPreview) : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          onClick={saveEdit}
                          isLoading={updateMutation.isPending}
                          disabled={!editDraft.description.trim()}
                        >
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                          Cancel
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              }
              const removable = line.kind === 'fee' || line.kind === 'subscription';
              return (
                <tr key={line.id}>
                  <td className="px-4 py-3">
                    <Badge tone="gray">{INVOICE_LINE_KIND_LABELS[line.kind]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{line.description}</td>
                  <td className="px-4 py-3">
                    <QboMappingCell value={mapping?.sku ?? null} source={mapping?.skuSource} />
                  </td>
                  <td className="px-4 py-3">
                    <QboMappingCell
                      value={mapping?.qboClass ?? null}
                      source={mapping?.classSource}
                    />
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600">{line.quantity}</td>
                  <td className="px-4 py-3 text-right text-slate-600">
                    {currency.format(Number(line.unitRate))}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">
                    {currency.format(Number(line.amount))}
                  </td>
                  {canEditLines ? (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {lines.length > 1 ? (
                          <div className="flex flex-col">
                            <button
                              type="button"
                              aria-label="Move line up"
                              className="px-1 text-xs leading-none text-slate-400 hover:text-slate-700 disabled:opacity-30"
                              disabled={index === 0 || reorderMutation.isPending}
                              onClick={() => moveLine(index, -1)}
                            >
                              ▲
                            </button>
                            <button
                              type="button"
                              aria-label="Move line down"
                              className="px-1 text-xs leading-none text-slate-400 hover:text-slate-700 disabled:opacity-30"
                              disabled={index === lines.length - 1 || reorderMutation.isPending}
                              onClick={() => moveLine(index, 1)}
                            >
                              ▼
                            </button>
                          </div>
                        ) : null}
                        <Button size="sm" variant="ghost" onClick={() => startEdit(line)}>
                          Edit
                        </Button>
                        {removable ? (
                          <Button size="sm" variant="ghost" onClick={() => setRemoveTarget(line)}>
                            Remove
                          </Button>
                        ) : (
                          <span
                            className="px-2 py-1 text-xs text-slate-400"
                            title="Time and overage lines are backed by hours — use the write-off button on the time entries below to drop hours."
                          >
                            via hours
                          </span>
                        )}
                      </div>
                    </td>
                  ) : null}
                </tr>
              );
            })
          )}
          {canEditLines && adding ? (
            <tr className="bg-slate-50">
              <td className="px-4 py-2">
                <ProductPicker
                  products={products}
                  value={addDraft.productId}
                  emptyLabel="No product"
                  ariaLabel="Product (optional)"
                  placeholder="Search products…"
                  inputClassName={LINE_INPUT_CLASS}
                  onChange={handleAddProductChange}
                />
              </td>
              <td className="px-4 py-2" colSpan={3}>
                <input
                  value={addDraft.description}
                  onChange={(e) => setAddDraft((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Description (e.g. Discount — first month)"
                  className={LINE_INPUT_CLASS}
                  aria-label="New line description"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={addDraft.quantity}
                  onChange={(e) => setAddDraft((p) => ({ ...p, quantity: e.target.value }))}
                  className={`${LINE_INPUT_CLASS} text-right`}
                  aria-label="New line quantity"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={addDraft.unitRate}
                  onChange={(e) => setAddDraft((p) => ({ ...p, unitRate: e.target.value }))}
                  placeholder="Negative = discount"
                  className={`${LINE_INPUT_CLASS} text-right`}
                  aria-label="New line rate"
                />
              </td>
              <td className="px-4 py-3 text-right font-medium text-slate-900">
                {currency.format(
                  Number(addDraft.quantity || '0') * Number(addDraft.unitRate || '0'),
                )}
              </td>
              <td className="px-4 py-2">
                <div className="flex justify-end gap-1">
                  <Button
                    size="sm"
                    onClick={saveAdd}
                    isLoading={addMutation.isPending}
                    disabled={!addDraft.description.trim()}
                  >
                    Add
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
                    Cancel
                  </Button>
                </div>
              </td>
            </tr>
          ) : null}
        </tbody>
        <tfoot className="border-t border-slate-200">
          {canEditLines && !adding ? (
            <tr>
              <td colSpan={cols} className="px-4 py-2">
                <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
                  Add line
                </Button>
              </td>
            </tr>
          ) : null}
          <tr>
            <td colSpan={cols - 1} className="px-4 py-3 text-right text-sm text-slate-500">
              Subtotal
            </td>
            <td className="px-4 py-3 text-right text-sm text-slate-700">
              {currency.format(Number(invoice.subtotal))}
            </td>
          </tr>
          <tr>
            <td
              colSpan={cols - 1}
              className="px-4 py-3 text-right text-sm font-semibold text-slate-900"
            >
              Total
            </td>
            <td className="px-4 py-3 text-right text-base font-semibold text-slate-900">
              {currency.format(Number(invoice.total))}
            </td>
          </tr>
        </tfoot>
      </table>

      <ConfirmDialog
        isOpen={removeTarget !== null}
        title="Remove line"
        message={
          removeTarget?.kind === 'subscription'
            ? `Remove "${removeTarget.description}"? Its agreement line's billed-through date rolls back, so this period will bill again on the next invoice run.`
            : `Remove "${removeTarget?.description ?? ''}" from this invoice?`
        }
        confirmLabel="Remove"
        isConfirming={removeMutation.isPending}
        onConfirm={() => {
          if (!removeTarget) return;
          removeMutation.mutate(
            { id: invoice.id, lineId: removeTarget.id },
            { onSuccess: () => setRemoveTarget(null) },
          );
        }}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}

/**
 * Renders a resolved QBO SKU or class with its source as a hover tooltip (e.g.
 * "Service category", "Ticket type", "Product", "QuickBooks settings (overage)").
 */
function QboMappingCell({
  value,
  source,
}: {
  value: string | null;
  source: QboLineMapping['skuSource'] | undefined;
}): JSX.Element {
  const sourceLabel = source ? QBO_MAPPING_SOURCE_LABELS[source] : undefined;
  if (!value) {
    const emptyTitle =
      source === 'qbo_default_item'
        ? 'No SKU resolved — the QuickBooks default item will be used'
        : 'Nothing will be sent for this line';
    return (
      <span className="text-slate-400" title={emptyTitle}>
        —
      </span>
    );
  }
  return (
    <span
      className="cursor-help font-mono text-xs text-slate-700"
      title={sourceLabel ? `From: ${sourceLabel}` : undefined}
    >
      {value}
    </span>
  );
}

interface InvoiceTimeEntriesProps {
  invoice: Invoice;
  canWrite: boolean;
}

/** Badge for a managed-services hour's included / overage / split allocation. */
function AllocationBadge({
  status,
}: {
  status: 'included' | 'overage' | 'split' | null;
}): JSX.Element {
  if (status === 'overage') return <Badge tone="amber">Overage</Badge>;
  if (status === 'split') return <Badge tone="blue">Included + overage</Badge>;
  if (status === 'included') return <Badge tone="green">Included</Badge>;
  return <span className="text-slate-400">—</span>;
}

/**
 * Lists the time entries linked to a draft/in-review project, tickets, or managed-
 * services invoice and lets a user write any of them off. Writing off removes the entry
 * from the invoice and recomputes the total server-side, so we refresh on success. For
 * managed services each hour also shows an Included / Overage allocation indicator.
 */
function InvoiceTimeEntries({ invoice, canWrite }: InvoiceTimeEntriesProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const editable = isInvoiceEditable(invoice.status);
  const relevant =
    invoice.source === 'project' ||
    invoice.source === 'tickets' ||
    invoice.source === 'managed_services';
  const query = useInvoiceTimeEntries(invoice.id, editable && relevant);
  const writeOff = useWriteOffTimeEntry();
  const entries = query.data ?? [];
  const showAllocation = invoice.source === 'managed_services';

  if (!editable || !relevant || entries.length === 0) {
    return null;
  }

  const handleWriteOff = (timeEntryId: string): void => {
    writeOff.mutate(
      { timeEntryId, input: { writtenOff: true } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ['invoices'] });
        },
      },
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">Time entries</h3>
        <p className="mt-1 text-xs text-slate-500">
          Writing off an entry removes it from this invoice and reduces the total.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Work date</th>
              <th className="px-4 py-2 font-medium">Member</th>
              <th className="px-4 py-2 font-medium">Ticket</th>
              <th className="px-4 py-2 text-right font-medium">Hours</th>
              {showAllocation ? <th className="px-4 py-2 font-medium">Allocation</th> : null}
              <th className="px-4 py-2 text-right font-medium">Rate</th>
              {canWrite ? <th className="px-4 py-2 font-medium">Actions</th> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td className="px-4 py-2 tabular-nums text-slate-600">
                  {formatDate(entry.workDate)}
                </td>
                <td className="px-4 py-2 text-slate-900">{entry.memberName}</td>
                <td className="px-4 py-2 text-slate-600">
                  {entry.ticketReferenceId ? (
                    <span className="mr-1 font-medium tabular-nums text-slate-900">
                      {entry.ticketReferenceId}
                    </span>
                  ) : null}
                  {entry.ticketTitle ?? (entry.ticketReferenceId ? '' : '—')}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-600">{entry.hours}</td>
                {showAllocation ? (
                  <td className="px-4 py-2">
                    <AllocationBadge status={entry.overageStatus ?? null} />
                  </td>
                ) : null}
                <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                  {entry.resolvedRate}
                </td>
                {canWrite ? (
                  <td className="px-4 py-2">
                    <Button
                      variant="secondary"
                      onClick={() => handleWriteOff(entry.id)}
                      isLoading={writeOff.isPending && writeOff.variables?.timeEntryId === entry.id}
                    >
                      Write off
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function InvoiceDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { can } = useAuth();
  const canWrite = can('invoices', 'edit');
  const canDelete = can('invoices', 'delete');
  const navigate = useNavigate();
  const backNav = useBackNavigation();
  const { inOverlay } = useDetailOverlay();

  const invoiceQuery = useInvoice(id);
  const reviewersQuery = useInvoiceReviewers();

  const updateMutation = useUpdateInvoice();
  const sendMutation = useSendInvoiceToQbo();
  const retryEmailMutation = useRetryQboInvoiceEmail();
  const sendToReviewMutation = useSendToReview();
  const requestChangesMutation = useRequestInvoiceChanges();
  const clearNeedsChangeMutation = useClearNeedsChange();
  const clearBillingReminderMutation = useClearCustomerBillingReminder();
  const closeMutation = useCloseInvoice();
  const holdMutation = useHoldInvoice();
  const releaseHoldMutation = useReleaseInvoiceHold();
  const reopenMutation = useReopenInvoice();
  const voidMutation = useVoidInvoice();
  const deleteMutation = useDeleteInvoice();
  const markBilledMutation = useMarkBilledAndRemoveInvoice();
  const previewMutation = usePreviewInvoicePdf();

  const invoice = invoiceQuery.data;
  const canAct = invoice ? isInvoiceEditable(invoice.status) : false;
  const isOnHold = invoice?.status === 'on_hold';
  useTrackRecent(
    invoice
      ? {
          entity: 'invoice',
          id: invoice.id,
          label: invoice.number,
          path: `/invoices/${invoice.id}`,
        }
      : null,
  );

  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [testEmail, setTestEmail] = useState(false);
  const [sendResult, setSendResult] = useState<QboBatchItemResult | null>(null);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [markBilledOpen, setMarkBilledOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestReviewerId, setRequestReviewerId] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [tab, setTab] = useState<'details' | 'history'>('details');

  useEffect(() => {
    setNotes(invoice?.notes ?? '');
    setInternalNotes(invoice?.internalNotes ?? '');
  }, [invoice?.id, invoice?.notes, invoice?.internalNotes]);

  const customerName = invoice?.customerName ?? '—';

  const reviewerOptions = useMemo(
    () => [
      { value: '', label: 'Unassigned' },
      ...(reviewersQuery.data ?? []).map((m) => ({ value: m.id, label: m.displayName })),
    ],
    [reviewersQuery.data],
  );

  if (invoiceQuery.isLoading || invoiceQuery.isError || !invoice) {
    return (
      <div>
        <PageHeader title="Invoice" />
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <DataState
            isLoading={invoiceQuery.isLoading}
            isError={invoiceQuery.isError}
            error={invoiceQuery.error}
            onRetry={() => void invoiceQuery.refetch()}
            isEmpty={!invoiceQuery.isLoading && !invoice}
            emptyTitle="Invoice not found"
            emptyDescription="This invoice may have been deleted."
          >
            <div />
          </DataState>
        </div>
      </div>
    );
  }

  const notesDirty = notes !== (invoice.notes ?? '');

  const handleSaveNotes = (): void => {
    updateMutation.mutate({ id: invoice.id, input: { notes: notes || null } });
  };

  const internalNotesDirty = internalNotes !== (invoice.internalNotes ?? '');

  const handleSaveInternalNotes = (): void => {
    updateMutation.mutate({ id: invoice.id, input: { internalNotes: internalNotes || null } });
  };

  const handleReviewerChange = (value: string): void => {
    updateMutation.mutate({ id: invoice.id, input: { activeReviewerId: value || null } });
  };

  const handleSend = (): void => {
    setSendResult(null);
    sendMutation.mutate(
      { id: invoice.id, input: { email: true, testEmail, dueDate: dueDate || null } },
      {
        onSuccess: (result) => {
          setSendResult(result);
          // A blocked or failed push leaves the invoice in draft, so keep the dialog
          // open with the reason rather than pretending the send happened.
          if (!result.error) {
            setSendOpen(false);
            setDueDate('');
          }
        },
      },
    );
  };

  const handleRetryEmail = (): void => {
    setSendResult(null);
    retryEmailMutation.mutate(
      { id: invoice.id, input: { testEmail: false } },
      { onSuccess: (result) => setSendResult(result) },
    );
  };

  const handleSendToReview = (reviewerEmail: string): void => {
    sendToReviewMutation.mutate({ id: invoice.id, input: { reviewerEmail } });
  };

  // The invoice after this one in the list the user opened it from (if any).
  const nextInvoiceId = nextInvoiceInSequence(invoice.id);

  const handleSendToReviewAndNext = (reviewerEmail: string): void => {
    sendToReviewMutation.mutate(
      { id: invoice.id, input: { reviewerEmail } },
      {
        onSuccess: () => {
          if (nextInvoiceId) navigate(`/invoices/${nextInvoiceId}`);
        },
      },
    );
  };

  const handleRequestChanges = (): void => {
    if (!requestReviewerId || !requestNote.trim()) return;
    requestChangesMutation.mutate(
      { id: invoice.id, input: { reviewerId: requestReviewerId, note: requestNote.trim() } },
      {
        onSuccess: () => {
          setRequestOpen(false);
          setRequestReviewerId('');
          setRequestNote('');
        },
      },
    );
  };

  const handleClearNeedsChange = (): void => {
    clearNeedsChangeMutation.mutate(invoice.id);
  };

  const handleClose = (): void => {
    closeMutation.mutate(invoice.id, {
      onSuccess: () => setCloseOpen(false),
    });
  };

  const handleReopen = (): void => {
    reopenMutation.mutate(invoice.id, {
      onSuccess: () => setReopenOpen(false),
    });
  };

  const handleVoid = (): void => {
    voidMutation.mutate(
      { id: invoice.id, input: { reason: voidReason } },
      {
        onSuccess: () => {
          setVoidOpen(false);
          setVoidReason('');
        },
      },
    );
  };

  const handleDelete = (): void => {
    deleteMutation.mutate(invoice.id, {
      onSuccess: () => navigate('/invoices'),
    });
  };

  const handleMarkBilled = (): void => {
    markBilledMutation.mutate(invoice.id, {
      onSuccess: () => navigate('/invoices'),
    });
  };

  const actionError =
    errorMessage(updateMutation.error, 'Failed to save changes.') ??
    errorMessage(sendMutation.error, 'Failed to send invoice.') ??
    errorMessage(retryEmailMutation.error, 'Failed to re-send the invoice email.') ??
    errorMessage(sendToReviewMutation.error, 'Failed to send invoice for review.') ??
    errorMessage(requestChangesMutation.error, 'Failed to request changes.') ??
    errorMessage(clearNeedsChangeMutation.error, 'Failed to clear the needs-change flag.') ??
    errorMessage(
      clearBillingReminderMutation.error,
      "Failed to clear the customer's billing reminder.",
    ) ??
    errorMessage(closeMutation.error, 'Failed to close invoice.') ??
    errorMessage(holdMutation.error, 'Failed to put the invoice on hold.') ??
    errorMessage(releaseHoldMutation.error, 'Failed to release the hold.') ??
    errorMessage(reopenMutation.error, 'Failed to reopen invoice.') ??
    errorMessage(voidMutation.error, 'Failed to void invoice.') ??
    errorMessage(deleteMutation.error, 'Failed to delete invoice.') ??
    errorMessage(markBilledMutation.error, 'Failed to mark billed and remove the draft.') ??
    errorMessage(previewMutation.error, 'Failed to open PDF.');

  const qboError = sendResult?.error ?? null;
  const emailNote = sendResult && !sendResult.error ? sendResult.emailNote : null;

  return (
    <div>
      {backNav.canGoBack ? (
        <button
          onClick={backNav.goBack}
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          ← {backNav.backLabel}
        </button>
      ) : inOverlay ? null : (
        <Link
          to="/invoices"
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          ← Back to invoices
        </Link>
      )}

      <PageHeader
        title={invoice.number}
        description={customerName}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => previewMutation.mutate(invoice)}
              isLoading={previewMutation.isPending}
            >
              <Icon name="invoices" className="h-4 w-4" />
              Preview PDF
            </Button>
            {invoice.needsChange ? <Badge tone="amber">Needs change</Badge> : null}
            {isOnHold ? <Badge tone="red">On hold</Badge> : null}
            {canWrite && (invoice.status === 'draft' || invoice.status === 'in_review') ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setRequestReviewerId(invoice.activeReviewerId ?? '');
                  setRequestNote('');
                  setRequestOpen(true);
                }}
              >
                Request changes
              </Button>
            ) : null}
            {canWrite && invoice.needsChange ? (
              <Button
                variant="ghost"
                onClick={handleClearNeedsChange}
                isLoading={clearNeedsChangeMutation.isPending}
              >
                Clear needs-change
              </Button>
            ) : null}
            {canWrite && canAct && invoice.source === 'manual' ? (
              <Button variant="secondary" onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            ) : null}
            {canWrite && canAct && !isOnHold ? (
              <>
                <Button
                  onClick={() => {
                    setSendResult(null);
                    setSendOpen(true);
                  }}
                  isLoading={sendMutation.isPending}
                >
                  Email and send to QBO
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleSendToReview(DAMON_EMAIL)}
                  isLoading={sendToReviewMutation.isPending}
                >
                  Send to Damon
                </Button>
                {nextInvoiceId ? (
                  <Button
                    variant="secondary"
                    onClick={() => handleSendToReviewAndNext(DAMON_EMAIL)}
                    isLoading={sendToReviewMutation.isPending}
                    title="Assign to Damon and open the next invoice from the list"
                  >
                    Send to Damon → next
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  onClick={() => handleSendToReview(JEFF_EMAIL)}
                  isLoading={sendToReviewMutation.isPending}
                >
                  Send to Jeff
                </Button>
                {nextInvoiceId ? (
                  <Button
                    variant="secondary"
                    onClick={() => handleSendToReviewAndNext(JEFF_EMAIL)}
                    isLoading={sendToReviewMutation.isPending}
                    title="Assign to Jeff and open the next invoice from the list"
                  >
                    Send to Jeff → next
                  </Button>
                ) : null}
                <Button variant="ghost" onClick={() => setCloseOpen(true)}>
                  Close without sending
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => holdMutation.mutate(invoice.id)}
                  isLoading={holdMutation.isPending}
                  title="Park this invoice — it cannot be emailed or sent to QuickBooks until the hold is released"
                >
                  Put on hold
                </Button>
              </>
            ) : null}
            {canWrite && isOnHold ? (
              <>
                <Button
                  onClick={() => releaseHoldMutation.mutate(invoice.id)}
                  isLoading={releaseHoldMutation.isPending}
                  title="Return the invoice to draft so it can be sent again"
                >
                  Release hold
                </Button>
                <Button variant="ghost" onClick={() => setCloseOpen(true)}>
                  Close without sending
                </Button>
              </>
            ) : null}
            {canWrite && invoice.status === 'sent' ? (
              <Button
                variant="secondary"
                onClick={handleRetryEmail}
                isLoading={retryEmailMutation.isPending}
              >
                Re-send email
              </Button>
            ) : null}
            {canWrite && invoice.status === 'sent' ? (
              <Button variant="danger" onClick={() => setVoidOpen(true)}>
                Void
              </Button>
            ) : null}
            {canWrite && (invoice.status === 'sent' || invoice.status === 'closed') ? (
              <Button
                variant="secondary"
                onClick={() => setReopenOpen(true)}
                isLoading={reopenMutation.isPending}
              >
                Reopen as draft
              </Button>
            ) : null}
          </div>
        }
      />

      {invoice.customerBillingReminder ? (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-semibold text-rose-700">
            <span className="uppercase tracking-wide">Billing reminder:</span>{' '}
            {invoice.customerBillingReminder}
          </p>
          {canWrite ? (
            <Button
              variant="secondary"
              onClick={() => clearBillingReminderMutation.mutate(invoice.id)}
              isLoading={clearBillingReminderMutation.isPending}
            >
              Clear reminder
            </Button>
          ) : null}
        </div>
      ) : null}

      <TabNav
        tabs={[
          { id: 'details', label: 'Details' },
          { id: 'history', label: 'History' },
        ]}
        activeId={tab}
        onChange={(id) => setTab(id as 'details' | 'history')}
      />

      {tab === 'history' ? <HistoryPanel entityType="Invoice" entityId={invoice.id} /> : null}

      <div className={tab === 'details' ? '' : 'hidden'}>
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Customer</dt>
              <dd className="mt-1 text-sm font-medium">
                <Link
                  to={`/customers?returnCustomerId=${invoice.customerId}&returnTab=invoices`}
                  className="text-brand-600 hover:underline"
                >
                  {customerName}
                </Link>
              </dd>
            </div>
            {invoice.agreementId ? (
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Agreement</dt>
                <dd className="mt-1 text-sm font-medium">
                  <Link
                    to={`/agreements/${invoice.agreementId}`}
                    className="text-brand-600 hover:underline"
                  >
                    {invoice.agreementName ?? 'Open agreement'}
                  </Link>
                </dd>
              </div>
            ) : null}
            {invoice.projectId ? (
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Project</dt>
                <dd className="mt-1 text-sm font-medium">
                  <Link
                    to={`/projects/${invoice.projectId}`}
                    className="text-brand-600 hover:underline"
                  >
                    {invoice.projectName ?? 'Open project'}
                  </Link>
                </dd>
              </div>
            ) : null}
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Status</dt>
              <dd className="mt-1 flex flex-wrap items-center gap-1.5">
                <Badge tone={INVOICE_STATUS_TONES[invoice.status]}>
                  {INVOICE_STATUS_LABELS[invoice.status]}
                </Badge>
                {invoice.paymentStatus ? (
                  <Badge tone={INVOICE_PAYMENT_STATUS_TONES[invoice.paymentStatus]}>
                    {INVOICE_PAYMENT_STATUS_LABELS[invoice.paymentStatus]}
                  </Badge>
                ) : null}
              </dd>
            </div>
            {invoice.paidAt ? (
              <DetailItem label="Paid (per QuickBooks)" value={formatDate(invoice.paidAt)} />
            ) : null}
            <DetailItem
              label="Period"
              value={
                invoice.periodStart && invoice.periodEnd
                  ? `${formatDate(invoice.periodStart)} → ${formatDate(invoice.periodEnd)}`
                  : '—'
              }
            />
            <DetailItem label="Issue date" value={formatDate(invoice.issueDate)} />
            <DetailItem label="Due date" value={formatDate(invoice.dueDate)} />
            <DetailItem label="Terms" value={invoice.terms ?? '—'} />
            <DetailItem label="Currency" value={invoice.currency} />
            <DetailItem
              label="Document type"
              value={INVOICE_DOCUMENT_TYPE_LABELS[invoice.documentType]}
            />
            <DetailItem label="Total" value={currency.format(Number(invoice.total))} />
            {invoice.qboInvoiceUrl ? (
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">
                  QuickBooks invoice
                </dt>
                <dd className="mt-1 text-sm font-medium">
                  <a
                    href={invoice.qboInvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline"
                  >
                    Open in QBO ↗
                  </a>
                </dd>
              </div>
            ) : null}
            {canWrite && canAct ? (
              <div>
                <SelectField
                  label="Active reviewer"
                  options={reviewerOptions}
                  value={invoice.activeReviewerId ?? ''}
                  onChange={(e) => handleReviewerChange(e.target.value)}
                />
              </div>
            ) : (
              <DetailItem label="Active reviewer" value={invoice.activeReviewerName ?? '—'} />
            )}
            {invoice.status === 'void' ? (
              <DetailItem label="Void reason" value={invoice.voidedReason ?? '—'} />
            ) : null}
          </dl>
        </div>

        {actionError ? (
          <div className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {actionError}
          </div>
        ) : null}

        {qboError ? (
          <div className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            Nothing was sent. {qboError} The invoice is still {invoice.status} — fix the problem
            and send again.
          </div>
        ) : null}

        {emailNote ? (
          <div className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
            Pushed to QuickBooks, but the email did not go out: {emailNote}
          </div>
        ) : null}

        {sendResult && !sendResult.error && sendResult.emailed ? (
          <div className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Pushed to QuickBooks and emailed to {sendResult.emailRecipients.join(', ')}.
          </div>
        ) : null}

        <div className="space-y-6">
          <InvoiceLines invoice={invoice} canEditLines={canWrite && canAct} />

          <InvoiceTimeEntries invoice={invoice} canWrite={canWrite} />

          <QboSyncLogPanel invoiceId={invoice.id} title="QuickBooks sync log" />

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">Notes</h3>
            {canWrite && canAct ? (
              <div className="space-y-3">
                <TextAreaField
                  label="Invoice notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes shown on the invoice."
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveNotes}
                    disabled={!notesDirty}
                    isLoading={updateMutation.isPending}
                  >
                    Save notes
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-600">{invoice.notes || '—'}</p>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">Internal notes</h3>
            {canWrite && canAct ? (
              <div className="space-y-3">
                <TextAreaField
                  label="Internal notes"
                  rows={3}
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Staff-only notes. Never shown on the customer invoice."
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveInternalNotes}
                    disabled={!internalNotesDirty}
                    isLoading={updateMutation.isPending}
                  >
                    Save internal notes
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-600">{invoice.internalNotes || '—'}</p>
            )}
          </div>

          {canDelete && (canAct || invoice.status === 'sent') ? (
            <div className="flex items-center justify-between gap-3">
              <Button variant="ghost" className="text-rose-600" onClick={() => setDeleteOpen(true)}>
                {canAct ? 'Delete draft invoice' : 'Delete invoice'}
              </Button>
              {canAct ? (
                <Button variant="secondary" onClick={() => setMarkBilledOpen(true)}>
                  Mark billed and remove draft
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        {sendOpen ? (
          <SendDialog
            invoiceId={invoice.id}
            number={invoice.number}
            dueDate={dueDate}
            onDueDateChange={setDueDate}
            testEmail={testEmail}
            onTestEmailChange={setTestEmail}
            isSending={sendMutation.isPending}
            failure={sendResult?.error ?? null}
            onConfirm={handleSend}
            onCancel={() => {
              setSendOpen(false);
              setDueDate('');
            }}
          />
        ) : null}

        <ConfirmDialog
          isOpen={closeOpen}
          title="Close without sending"
          message={`Close ${invoice.number} without emailing or pushing to QuickBooks? It will move to the Closed status and no further sending actions will be available.`}
          confirmLabel="Close invoice"
          isConfirming={closeMutation.isPending}
          onConfirm={handleClose}
          onCancel={() => setCloseOpen(false)}
        />

        <ConfirmDialog
          isOpen={reopenOpen}
          title="Reopen as draft"
          message={`Reopen ${invoice.number} as a draft so it can be edited and resent? Remember to remove it from QuickBooks by hand — its QuickBooks link will be cleared. Its lines and time entries are kept.`}
          confirmLabel="Reopen as draft"
          isConfirming={reopenMutation.isPending}
          onConfirm={handleReopen}
          onCancel={() => setReopenOpen(false)}
        />

        <ConfirmDialog
          isOpen={deleteOpen}
          title={canAct ? 'Delete draft invoice' : 'Delete invoice'}
          message={
            invoice.status === 'sent'
              ? `Delete sent invoice ${invoice.number}? PSA will release its time entries back to billable and roll back its billing cursors. Remember to also delete it in QuickBooks. This cannot be undone.`
              : `Delete ${invoice.number}? This cannot be undone. You can regenerate it from the closed period.`
          }
          confirmLabel="Delete invoice"
          destructive
          error={errorMessage(deleteMutation.error, 'Failed to delete the invoice.')}
          isConfirming={deleteMutation.isPending}
          onConfirm={handleDelete}
          onCancel={() => setDeleteOpen(false)}
        />

        <ConfirmDialog
          isOpen={markBilledOpen}
          title="Mark billed and remove draft"
          message={`Remove draft ${invoice.number} and mark the work it covers as billed outside PSA? The billing cursors stay advanced and the covered time entries will no longer appear as ready to invoice. This cannot be undone.`}
          confirmLabel="Mark billed and remove"
          error={errorMessage(markBilledMutation.error, 'Failed to mark billed and remove the draft.')}
          isConfirming={markBilledMutation.isPending}
          onConfirm={handleMarkBilled}
          onCancel={() => setMarkBilledOpen(false)}
        />

        {voidOpen ? (
          <VoidDialog
            number={invoice.number}
            reason={voidReason}
            onReasonChange={setVoidReason}
            isVoiding={voidMutation.isPending}
            onConfirm={handleVoid}
            onCancel={() => setVoidOpen(false)}
          />
        ) : null}

        <InvoiceFormModal
          isOpen={editOpen}
          invoice={invoice}
          documentType={invoice.documentType}
          onClose={() => setEditOpen(false)}
        />

        <Modal
          isOpen={requestOpen}
          onClose={() => setRequestOpen(false)}
          title="Request changes"
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRequestOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleRequestChanges}
                disabled={
                  !requestReviewerId || !requestNote.trim() || requestChangesMutation.isPending
                }
                isLoading={requestChangesMutation.isPending}
              >
                Flag &amp; email reviewer
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Flags this invoice as needing changes, appends your note to the internal notes,
              routes it to the reviewer, and emails them the invoice details and note.
            </p>
            <SelectField
              label="Reviewer"
              options={[
                { value: '', label: 'Choose reviewer…' },
                ...(reviewersQuery.data ?? []).map((m) => ({ value: m.id, label: m.displayName })),
              ]}
              value={requestReviewerId}
              onChange={(e) => setRequestReviewerId(e.target.value)}
            />
            <TextAreaField
              label="Change request note"
              value={requestNote}
              onChange={(e) => setRequestNote(e.target.value)}
              rows={4}
              placeholder="Describe what needs to change…"
            />
          </div>
        </Modal>
      </div>
    </div>
  );
}

interface VoidDialogProps {
  number: string;
  reason: string;
  onReasonChange: (value: string) => void;
  isVoiding: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function VoidDialog({
  number,
  reason,
  onReasonChange,
  isVoiding,
  onConfirm,
  onCancel,
}: VoidDialogProps): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
        onClick={onCancel}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl">
        <header className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Void {number}</h2>
        </header>
        <div className="px-5 py-4">
          <p className="mb-3 text-sm text-slate-600">
            Voiding marks this invoice as cancelled. Provide a reason for the audit trail.
          </p>
          <TextField
            label="Reason"
            required
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="e.g. Issued in error"
          />
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <Button variant="secondary" onClick={onCancel} disabled={isVoiding}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            isLoading={isVoiding}
            disabled={reason.trim().length === 0}
          >
            Void invoice
          </Button>
        </footer>
      </div>
    </div>
  );
}

interface SendDialogProps {
  invoiceId: string;
  number: string;
  dueDate: string;
  onDueDateChange: (value: string) => void;
  testEmail: boolean;
  onTestEmailChange: (value: boolean) => void;
  isSending: boolean;
  /** Why the last attempt failed, shown in place so the operator can fix and retry. */
  failure: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function SendDialog({
  invoiceId,
  number,
  dueDate,
  onDueDateChange,
  testEmail,
  onTestEmailChange,
  isSending,
  failure,
  onConfirm,
  onCancel,
}: SendDialogProps): JSX.Element {
  const previewQuery = useQboSendPreview(invoiceId);
  const preview = previewQuery.data;
  const blockers = preview?.blockers ?? [];
  const blocked = blockers.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
        onClick={onCancel}
      />
      <div className="relative z-10 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white shadow-xl">
        <header className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Send {number}</h2>
        </header>
        <div className="space-y-4 px-5 py-4">
          <p className="text-sm text-slate-600">
            The invoice is checked, pushed to QuickBooks, and only then emailed. If anything
            fails, nothing is emailed and the invoice stays where it is.
          </p>

          {previewQuery.isLoading ? (
            <p className="text-sm text-slate-500">Checking the invoice…</p>
          ) : null}

          {blocked ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
              <p className="text-sm font-medium text-rose-800">This invoice cannot be sent:</p>
              <ul className="mt-1 list-disc pl-5 text-sm text-rose-700">
                {blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {failure ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {failure}
            </div>
          ) : null}

          {preview ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <RecipientList label="To" emails={testEmail ? [preview.testMailbox] : preview.emailTo} />
              {testEmail ? null : <RecipientList label="Cc" emails={preview.emailCc} />}
              {preview.dryRun ? (
                <p className="mt-2 text-xs text-amber-700">
                  QuickBooks is in dry-run mode: the payload is recorded but never posted, and
                  no email is sent.
                </p>
              ) : null}
            </div>
          ) : null}

          <TextField
            label="Due date"
            type="date"
            value={dueDate}
            onChange={(e) => onDueDateChange(e.target.value)}
          />
          <p className="-mt-3 text-xs text-slate-500">
            Leave blank to default to the issue date plus standard net terms.
          </p>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={testEmail}
              onChange={(e) => onTestEmailChange(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            Test email (send only to {preview?.testMailbox ?? 'the shared billing mailbox'})
          </label>
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <Button variant="secondary" onClick={onCancel} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={onConfirm} isLoading={isSending} disabled={blocked || previewQuery.isLoading}>
            Email and send to QBO
          </Button>
        </footer>
      </div>
    </div>
  );
}

/** One read-only recipient line in the send dialog. */
function RecipientList({ label, emails }: { label: string; emails: string[] }): JSX.Element {
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-7 shrink-0 font-medium text-slate-500">{label}</span>
      <span className={emails.length > 0 ? 'text-slate-800' : 'text-slate-400'}>
        {emails.length > 0 ? emails.join(', ') : 'No recipients'}
      </span>
    </div>
  );
}
